import { InAppNotificationService } from "../services/in-app-notification.service.js";
import { PlazaService } from "../services/plaza.service.js";
import { ContentService } from "../services/content.service.js";
import { BlackHoleModal } from "./modals/BlackHoleModal.js";

export const NotificationListModal = {
    show: async (uid) => {
        // Create Modal Elements
        // Create Elements (Split DOM for Sandwich)
        const overlay = document.createElement('div');
        overlay.className = 'fixed inset-0 bg-black/50 z-[9990] fade-in'; // Behind Header

        const wrapper = document.createElement('div');
        wrapper.className = 'fixed inset-0 z-[10001] flex items-center justify-center sm:justify-center pointer-events-none'; // Top Layer

        const modal = document.createElement('div');
        modal.className = 'bg-white w-full sm:w-[400px] h-full sm:h-[600px] sm:rounded-xl shadow-2xl flex flex-col sm:mr-4 sm:mt-16 overflow-hidden slide-in-right pointer-events-auto'; // Content clickable

        modal.innerHTML = `
            <div class="px-4 py-3 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
                <h3 class="font-bold text-gray-900">알림</h3>
                <div class="flex gap-2">
                    <button id="btn-read-all" class="text-xs text-blue-600 font-medium hover:bg-blue-50 px-2 py-1 rounded">모두 읽음</button>
                    <button id="btn-close-notif" class="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                        <span class="material-symbols-outlined text-xl">close</span>
                    </button>
                </div>
            </div>
            <div id="notif-list-container" class="flex-grow overflow-y-auto p-0 bg-gray-50">
                <div class="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
                    <span class="loader-spin w-6 h-6 border-gray-300 border-t-blue-500"></span>
                </div>
            </div>
        `;

        wrapper.appendChild(modal);
        document.body.appendChild(overlay);
        document.body.appendChild(wrapper);

        // Handlers
        const close = () => {
            if (overlay.isConnected) overlay.remove();
            if (wrapper.isConnected) wrapper.remove();
        };
        overlay.onclick = () => close();
        modal.querySelector('#btn-close-notif').onclick = close;

        const listContainer = modal.querySelector('#notif-list-container');
        const readAllBtn = modal.querySelector('#btn-read-all');

        // Logic
        const load = async () => {
            const notifs = await InAppNotificationService.getNotifications(uid, 50);

            if (notifs.length === 0) {
                listContainer.innerHTML = `
                    <div class="flex flex-col items-center justify-center h-full text-gray-400">
                        <span class="material-symbols-outlined text-4xl mb-2 opacity-20">notifications_off</span>
                        <span class="text-sm">새로운 알림이 없습니다.</span>
                    </div>
                `;
                return;
            }

            listContainer.innerHTML = '';
            notifs.forEach(item => {
                const el = document.createElement('div');
                el.className = `p-4 border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors ${item.isRead ? 'bg-white opacity-60' : 'bg-blue-50/30'}`;

                // Icon based on type
                let icon = 'notifications';
                let iconColor = 'text-gray-400';
                if (item.type === 'like') { icon = 'favorite'; iconColor = 'text-red-500'; }
                else if (item.type === 'comment') { icon = 'chat_bubble'; iconColor = 'text-blue-500'; }
                else if (item.type === 'mention') { icon = 'alternate_email'; iconColor = 'text-violet-500'; }
                else if (item.type === 'follow') { icon = 'person_add'; iconColor = 'text-green-500'; }
                else if (item.type === 'guestbook') { icon = 'import_contacts'; iconColor = 'text-purple-500'; }
                else if (item.type === 'point') { icon = 'savings'; iconColor = 'text-yellow-500'; } // [NEW] Point Icon
                else if (item.type === 'system' || item.type === 'notice') { icon = 'campaign'; iconColor = 'text-blue-600'; } // [NEW] Notice Icon
                else if (item.type === 'episode') { icon = 'menu_book'; iconColor = 'text-indigo-600'; } // [NEW] Episode Icon

                const date = item.createdAt ? new Date(item.createdAt.toDate()).toLocaleDateString() : '';

                el.innerHTML = `
                    <div class="flex gap-3">
                        <div class="mt-1 relative">
                            ${item.fromSnapshot?.photoURL
                        ? `<img src="${item.fromSnapshot.photoURL}" 
                                class="w-10 h-10 rounded-full object-cover border border-gray-100" 
                                onerror="this.onerror=null; this.src='/logo.png';">`
                        : `<div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center"><span class="material-symbols-outlined text-gray-500">person</span></div>`
                    }
                            <div class="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
                                <span class="material-symbols-outlined text-xs ${iconColor}">${icon}</span>
                            </div>
                        </div>
                        <div class="flex-grow min-w-0">
                            <p class="text-sm text-gray-800 leading-snug break-words ${item.isRead ? '' : 'font-semibold'}">${item.message}</p>
                            <span class="text-xs text-gray-400 mt-1 block">${date}</span>
                        </div>
                        ${!item.isRead ? `<div class="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>` : ''}
                    </div>
                `;

                el.onclick = async (e) => {
                    if (e) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                    }

                    // [Safety] Prevent Double Clicks
                    if (el.dataset.processing === 'true') return;
                    el.dataset.processing = 'true';

                    // [OPTIMIZATION] Immediate Navigation (No Blocking Check)
                    // We trust the link. If it's 404, the target page or Router will handle it.

                    try {
                        if (!item.isRead) {
                            InAppNotificationService.markAsRead(uid, item.id).catch(err => console.warn('Mark read failed', err));
                        }
                    } catch (e) { console.warn(e); }

                    close();

                    if (item.targetLink) {
                        // [Fix v1.36.469] Smart Link Reconstruction (Handle Legacy /viewer/:id)
                        let target = item.targetLink;

                        // Check for Legacy "Viewer" Link
                        if (target.includes('/viewer/')) {
                            // Extract Episode ID
                            const parts = target.split('/');
                            const episodeId = parts[parts.length - 1]; // Last part

                            // Check for Series ID in Metadata (Legacy) or Top-level (New)
                            // Legacy: item.metadata.seriesId
                            // New: item.seriesId
                            const seriesId = item.seriesId || (item.metadata && item.metadata.seriesId);

                            if (seriesId && episodeId) {
                                console.log(`[Notification] Auto-correcting Legacy Link: ${target} -> /series/${seriesId}/episode/${episodeId}`);
                                target = `/series/${seriesId}/episode/${episodeId}`;
                            } else {
                                console.warn("[Notification] Legacy Link detected but missing Series ID. Navigation may fail.");
                            }
                        }

                        // [Fix] Normalize Admin Shortcut
                        if (target === '/admin') {
                            target = '/admin/content';
                        }

                        // [Optimization] Check if we are already there to avoid Router overhead
                        // Decode both to ensure matches (e.g. Korean chars)
                        const currentHash = window.location.hash.replace('#', '');
                        const normalize = (s) => {
                            try { return decodeURIComponent(s).replace(/\/$/, ''); }
                            catch (e) { return s.replace(/\/$/, ''); }
                        };

                        if (normalize(currentHash) === normalize(target)) {
                            console.log("[Notification] Already on target page. Modal closed.");
                            return;
                        }

                        console.log("[Notification] Navigating to:", target);
                        window.router.navigate(target);
                    }
                };

                listContainer.appendChild(el);
            });
        };

        readAllBtn.onclick = async () => {
            readAllBtn.innerText = "처리 중...";
            InAppNotificationService.markAllAsRead(uid);
            await load(); // Reload list
            readAllBtn.innerText = "모두 읽음";
        };

        await load();
    }
};
